/**
 * Row schemas for the APP domain stores (`checkpoints`, `sync_log`,
 * `flight_snapshots`, `app_metadata`).
 *
 * See rows/static.ts for the shared posture. Notes:
 * - `sync_log` is a dead store (zero readers/writers) — its schema is FROZEN
 *   as-is, awaiting removal in a future IDB version bump (see the operator
 *   hand-off list in plan/overhaul/README.md). Do not extend it here.
 * - `app_metadata` is the schema-evolution envelope: `schemaHistory`
 *   (appended on every versionchange upgrade), the `legacy-recovery-v25`
 *   straggler snapshot, and one-time maintenance flags. The typed envelope
 *   lives below.
 */
import { z } from 'zod';
import type { SyncCheckpoint, SyncLogEntry } from '~types/sync';
import type { FlightSnapshot } from '~types/flight-recorder';

/** `checkpoints` row (key: autoincrement id). */
export const syncCheckpointRowSchema = z.looseObject({
  id: z.number(),
  timestamp: z.number(),
  blob: z.custom<Uint8Array>((v) => v instanceof Uint8Array, {
    message: 'Expected Uint8Array',
  }),
  size: z.number(),
  trigger: z.string(),
  /**
   * Pin against the rolling prune (pre-migration backups). Additive field:
   * rows persisted before it exist without it and are unprotected.
   */
  protected: z.boolean().optional(),
});
export type SyncCheckpointRow = {
  id: number;
  timestamp: number;
  blob: Uint8Array;
  size: number;
  trigger: string;
  protected?: boolean;
};

/** `sync_log` row — FROZEN (dead store, see module docs). */
export const syncLogEntryRowSchema = z.looseObject({
  id: z.number(),
  timestamp: z.number(),
  level: z.enum(['info', 'warn', 'error']),
  message: z.string(),
  details: z.unknown().optional(),
});
export type SyncLogEntryRow = {
  id: number;
  timestamp: number;
  level: 'info' | 'warn' | 'error';
  message: string;
  details?: unknown;
};

/** `flight_snapshots` row (key: uuid id). */
export const flightSnapshotRowSchema = z.looseObject({
  id: z.string().min(1),
  createdAt: z.number(),
  trigger: z.string(),
  note: z.string(),
  context: z.looseObject({
    bookId: z.string().nullable(),
    sectionIndex: z.number(),
    currentIndex: z.number(),
    queueLength: z.number(),
    status: z.string(),
    skippedCount: z.number().optional(),
    nextItemSkipped: z.boolean().optional(),
  }),
  eventCount: z.number(),
  timeRange: z.looseObject({ first: z.number(), last: z.number() }),
  eventsJSON: z.string(),
  sizeBytes: z.number(),
});
export type FlightSnapshotRow = {
  id: string;
  createdAt: number;
  trigger: string;
  note: string;
  context: {
    bookId: string | null;
    sectionIndex: number;
    currentIndex: number;
    queueLength: number;
    status: string;
    skippedCount?: number;
    nextItemSkipped?: boolean | undefined;
  };
  eventCount: number;
  timeRange: { first: number; last: number };
  eventsJSON: string;
  sizeBytes: number;
};

// ── app_metadata: the schema-evolution envelope ───────────────────────────

/**
 * Keys of the `app_metadata` key-value store (out-of-line keys). Keys are
 * append-only — never reuse a retired key for a different shape.
 */
export const APP_METADATA_KEYS = {
  /** `SchemaHistoryEntry[]` — one entry appended per versionchange upgrade. */
  schemaHistory: 'schemaHistory',
  /**
   * `LegacyRecoveryRecord` — the v25 straggler guard's snapshot-before-delete
   * of surviving v17/v18 user-data stores (recoverable via support/
   * diagnostics; retention is an open operator decision, see the hand-off
   * list in plan/overhaul/README.md).
   */
  legacyRecoveryV25: 'legacy-recovery-v25',
  /** `true` once the post-open idle audio `size` backfill completed (v25). */
  audioSizeBackfillV25: 'audio-size-backfill-v25',
  /**
   * `QuotaDailyUsageRow` — the AI-API rate limiter's persisted daily request
   * count for the current Pacific-time day. Lives under this existing
   * key-value store (no dedicated store, no DB bump): last-write-wins, and a
   * stored row whose day-string differs from today counts as zero (daily
   * rollover). Read/written only by src/data/repos/quotaCounter.ts.
   */
  quotaDailyUsage: 'quota-daily-usage',
  /**
   * `number` — the audio cache's running byte total (sum of every
   * `cache_audio_blobs` row's `size`). A HINT, not a ledger: the eviction
   * sweep reads it to skip the full store scan while the cache is provably
   * under budget, and any sweep that does scan re-establishes it from the
   * rows themselves. A stale-high value costs one extra scan; a stale-low
   * value delays a sweep until the next scan corrects it. Never used to
   * decide WHAT to delete (only the scan's own totals are).
   * Read/written only by src/data/repos/audioCache.ts.
   */
  audioCacheTotalBytes: 'audio-cache-total-bytes',
  /**
   * `number` — the embedding cache's running byte total (sum of every
   * `cache_embeddings` row's packed `vectors`/`scales` byteLengths). Same
   * HINT contract as {@link audioCacheTotalBytes}: it only ever decides
   * whether the eviction sweep may SKIP its full scan, never what to delete,
   * and any sweep that does scan re-establishes it from the rows themselves.
   * A put that replaces an existing book's row over-counts (the old row's
   * bytes are still included), which can only buy one earlier full scan.
   * Read/written only by src/data/repos/embeddings.ts.
   */
  embeddingCacheTotalBytes: 'embedding-cache-total-bytes',
  /**
   * `number` — the `TTS_EXTRACTION_VERSION` at which the background re-ingest
   * wave last finished with ZERO candidate books (a converged library). Later
   * boots skip the whole-store candidacy scan
   * (`bookContent.listTtsExtractionVersions`) while this equals the current
   * constant; bumping the extraction version makes the stored value stale and
   * the wave scans again. Written only after a completed, non-aborted wave.
   */
  reingestScanConverged: 'reingest-scan-converged',
} as const;

/**
 * One `schemaHistory` entry: a versionchange upgrade that ran to completion.
 * @public C1 row contract: no parse call site yet — kept exported as the
 * drift-guard anchor (`_HistorySchemaMatches` below pins it to the type).
 */
export const schemaHistoryEntrySchema = z.looseObject({
  from: z.number(),
  to: z.number(),
  at: z.number(),
});
export type SchemaHistoryEntry = { from: number; to: number; at: number };

/**
 * One legacy store's captured rows inside the recovery record. `rowsJSON`
 * is a JSON array string (rows serialized one by one so the size cap can
 * stop mid-store); binary fields are elided to
 * `{ __binary: <ctor>, byteLength }` descriptors.
 */
const legacyRecoveryStoreCaptureSchema = z.looseObject({
  store: z.string(),
  rowCount: z.number(),
  capturedCount: z.number(),
  rowsJSON: z.string(),
});
export type LegacyRecoveryStoreCapture = {
  store: string;
  rowCount: number;
  capturedCount: number;
  rowsJSON: string;
};

/**
 * The `legacy-recovery-v25` record (the v25 straggler guard).
 * @public C1 row contract: no parse call site yet — kept exported as the
 * drift-guard anchor (`_RecoverySchemaMatches` below pins it to the type).
 */
export const legacyRecoveryRecordSchema = z.looseObject({
  capturedAt: z.number(),
  fromVersion: z.number(),
  truncated: z.boolean(),
  stores: z.array(legacyRecoveryStoreCaptureSchema),
});
export type LegacyRecoveryRecord = {
  capturedAt: number;
  fromVersion: number;
  truncated: boolean;
  stores: LegacyRecoveryStoreCapture[];
};

/**
 * The `quota-daily-usage` record: the AI-API rate limiter's persisted daily
 * request count. `day` is the Pacific-time day key (`YYYY-MM-DD`); a stored
 * row whose `day` differs from today is treated as zero, giving a once-a-day
 * rollover. Mirrors the in-memory usage shape the limiter tracks, so the
 * adapter that owns the limiter can map between them.
 * @public C1 row contract: parsed via the quotaCounter repo; pinned to the type
 * by `_QuotaSchemaMatches` below.
 */
export const quotaDailyUsageSchema = z.looseObject({
  day: z.string(),
  pools: z.record(z.string(), z.looseObject({
    rpd: z.number(),
    tpm: z.number().optional(),
  })).optional(),
  rpd: z.number().optional(),
  tpm: z.number().optional(),
});
export type QuotaDailyUsageRow = {
  day: string;
  pools?: Record<string, { rpd: number; tpm?: number }>;
  rpd?: number;
  tpm?: number;
};

/**
 * Everything `app_metadata` may hold — the typed envelope. Readers narrow by
 * key (see APP_METADATA_KEYS).
 */
export type AppMetadataValue =
  | SchemaHistoryEntry[]
  | LegacyRecoveryRecord
  | QuotaDailyUsageRow
  | boolean
  | number;

// ── Compile-time drift guards (see rows/static.ts for the pattern) ────────
type _CheckpointSchemaMatches = z.infer<typeof syncCheckpointRowSchema> extends SyncCheckpointRow ? true : never;
type _LogSchemaMatches = z.infer<typeof syncLogEntryRowSchema> extends SyncLogEntryRow ? true : never;
type _SnapshotSchemaMatches = z.infer<typeof flightSnapshotRowSchema> extends FlightSnapshotRow ? true : never;
type _CheckpointRound = SyncCheckpointRow extends SyncCheckpoint ? true : never;
type _SnapshotRound = FlightSnapshotRow extends FlightSnapshot ? true : never;
type _HistorySchemaMatches = z.infer<typeof schemaHistoryEntrySchema> extends SchemaHistoryEntry ? true : never;
type _RecoverySchemaMatches = z.infer<typeof legacyRecoveryRecordSchema> extends LegacyRecoveryRecord ? true : never;
type _QuotaSchemaMatches = z.infer<typeof quotaDailyUsageSchema> extends QuotaDailyUsageRow ? true : never;
const _schemaChecks: [
  _CheckpointSchemaMatches,
  _LogSchemaMatches,
  _SnapshotSchemaMatches,
  _CheckpointRound,
  _SnapshotRound,
  _HistorySchemaMatches,
  _RecoverySchemaMatches,
  _QuotaSchemaMatches,
] = [true, true, true, true, true, true, true, true];
void _schemaChecks;

function _rowTypeDriftGuard(
  checkpoint: SyncCheckpoint,
  logEntry: SyncLogEntry,
  snapshot: FlightSnapshot,
): void {
  const _checkpoint: SyncCheckpointRow = checkpoint;
  const _logEntry: SyncLogEntryRow = logEntry;
  const _snapshot: FlightSnapshotRow = snapshot;
  void _checkpoint; void _logEntry; void _snapshot;
}
void _rowTypeDriftGuard;
